import { Skeleton, Text, TextSize } from '@vizit/atoms';
import { AlertCircleIcon, CheckCircleIcon, LightbulbIcon } from '@vizit/icons';
import { ScoreDiagram } from './score-diagram';
import type { ScoreExplainProps } from './types';

/**
 * Loading skeleton for a single factor item
 */
function FactorSkeleton() {
  return (
    <div className="flex items-center justify-between gap-4">
      <Skeleton className="h-4 w-20" variant="square" />
      <Skeleton className="h-[18px] w-[18px]" variant="rounded" />
    </div>
  );
}

/**
 * Score explanation component that displays in a popover
 * Shows the conversion score breakdown with visual diagram and contributing factors
 */
export function ScoreExplain(props: ScoreExplainProps) {
  const { score, factors, isScoreLoading = false, isFactorsLoading = false } = props;

  const { heroImage = 'alert', carouselScore = 'alert', assetMix = 'alert', assetCount = 'alert' } = factors || {};

  return (
    <div className="bg-primary flex flex-col gap-5 p-5 pb-8 shadow-lg min-w-[320px] max-w-[320px] text-left">
      <div className="w-14 h-14 flex items-center justify-center border border-primary p-2">
        <LightbulbIcon size={16} className="text-tertiary" />
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col">
          {isScoreLoading ? (
            <>
              <Skeleton className="h-4 w-full mb-1" variant="square" />
              <Skeleton className="h-4 w-3/4 mb-4" variant="square" />
              <Skeleton className="h-[60px] w-full" variant="square" />
            </>
          ) : (
            <>
              <Text size={TextSize.Sm} className="text-secondary" showEllipsis={false}>
                This product page is likely to convert better than {score}% of other products in the same category.
              </Text>

              <ScoreDiagram score={score} />
            </>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <div className="border-t border-border-secondary" />

          <Text size={TextSize.Sm} className="text-primary" showEllipsis={false}>
            The following factors are taken into consideration when scoring a product:
          </Text>

          <div className="grid grid-cols-2 gap-x-10 gap-y-4">
            {isFactorsLoading ? (
              <>
                <FactorSkeleton />
                <FactorSkeleton />
                <FactorSkeleton />
                <FactorSkeleton />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4">
                  <Text size={TextSize.Sm} className="text-tertiary" showEllipsis={false}>
                    Hero image:
                  </Text>
                  {heroImage === 'pass' ? (
                    <CheckCircleIcon size={18} className="text-success-500 shrink-0" />
                  ) : (
                    <AlertCircleIcon size={18} className="text-tertiary shrink-0" />
                  )}
                </div>

                <div className="flex items-center justify-between gap-4">
                  <Text size={TextSize.Sm} className="text-tertiary" showEllipsis={false}>
                    Carousel score
                  </Text>
                  {carouselScore === 'pass' ? (
                    <CheckCircleIcon size={18} className="text-success-500 shrink-0" />
                  ) : (
                    <AlertCircleIcon size={18} className="text-tertiary shrink-0" />
                  )}
                </div>

                <div className="flex items-center justify-between gap-2">
                  <Text size={TextSize.Sm} className="text-tertiary" showEllipsis={false}>
                    Asset mix:
                  </Text>
                  {assetMix === 'pass' ? (
                    <CheckCircleIcon size={18} className="text-success-500 shrink-0" />
                  ) : (
                    <AlertCircleIcon size={18} className="text-tertiary shrink-0" />
                  )}
                </div>

                <div className="flex items-center justify-between gap-2">
                  <Text size={TextSize.Sm} className="text-tertiary" showEllipsis={false}>
                    Asset count:
                  </Text>
                  {assetCount === 'pass' ? (
                    <CheckCircleIcon size={18} className="text-success-500 shrink-0" />
                  ) : (
                    <AlertCircleIcon size={18} className="text-tertiary shrink-0" />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="border-t border-border-secondary" />
        </div>
      </div>

      <Text size={TextSize.Sm} className="text-primary" showEllipsis={false}>
        We also factor in key signals that influence shopper trust and conversion such
        <span className="font-medium"> as price tier, star rating, and volume of reviews</span>.
      </Text>
    </div>
  );
}
